import { useCallback } from "react";

import { ApolloError, FetchResult, useMutation } from "@apollo/client";

import {
  DASHBOARD,
  DASHBOARDS,
  UPDATE_DASHBOARD,
  WORKSPACE,
  WORKSPACES,
  WorkspaceClientName
} from "api/workspace";

import { Dashboard, DashboardUpdate, DashboardWidgetUpdate } from "models/workspace";

import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";

import { getSessionData } from "../utils/getSessionData";

interface UseSaveDashboardOptions {
  onSuccess?: () => void;
  onError?: (error: ApolloError) => void;
}

export type UseSaveDashboardReturnT = {
  mutate: (dashboard: Dashboard) => Promise<FetchResult | undefined>;
  isLoading: boolean;
};

export function useSaveDashboard({
  onSuccess,
  onError
}: UseSaveDashboardOptions = {}): UseSaveDashboardReturnT {
  const { workspace } = useWorkspaceContext();
  const [updateDashboard, { loading }] = useMutation(UPDATE_DASHBOARD);

  const mutate = useCallback(
    async (dashboard: Dashboard) => {
      if (!dashboard || !workspace) return;

      // Use the session storage cache of the chart settings to update the dashboard.
      const updatedWidgets = dashboard.widgets?.map((widget) => {
        const updatedWidget: DashboardWidgetUpdate = {
          widgetId: widget.widgetId,
          title: widget.title,
          type: widget.type,
          x: widget.x,
          y: widget.y,
          width: widget.width,
          height: widget.height,
          settings: widget.settings
        };
        const sessionData = getSessionData(widget);
        if (sessionData) {
          updatedWidget.settings = sessionData;
        }

        return updatedWidget;
      });

      // Map to the update dashboard model.
      const updatedDashboard: DashboardUpdate = {
        dashboardId: dashboard.dashboardId,
        parentId: dashboard.parentId,
        title: dashboard.title,
        layout: {
          format: dashboard.layout.format,
          width: dashboard.layout.width,
          height: dashboard.layout.height
        },
        widgets: updatedWidgets
      };

      return updateDashboard({
        variables: {
          dashboard: updatedDashboard
        },
        context: {
          clientName: WorkspaceClientName
        },
        refetchQueries: [
          {
            query: DASHBOARD,
            variables: { id: dashboard.dashboardId },
            context: { clientName: WorkspaceClientName }
          },
          { query: DASHBOARDS, context: { clientName: WorkspaceClientName } },
          { query: WORKSPACES, context: { clientName: WorkspaceClientName } },
          {
            query: WORKSPACE,
            variables: { id: workspace.workspaceId },
            context: { clientName: WorkspaceClientName }
          }
        ],
        onCompleted: () => {
          onSuccess?.();
        },
        onError: (error: ApolloError) => {
          // eslint-disable-next-line no-console
          console.error(error.message, error);
          onError?.(error);
        }
      });
    },
    [updateDashboard, onSuccess, onError, workspace]
  );

  return { mutate, isLoading: loading };
}
